import { clsx } from 'clsx'
import { type ButtonHTMLAttributes, forwardRef, type ReactNode } from 'react'

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger'

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant
  size?: 'sm' | 'md' | 'lg'
  icon?: ReactNode
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', icon, className, children, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      className={clsx(
        'inline-flex select-none items-center justify-center gap-2 whitespace-nowrap rounded-xl font-medium transition-[background,color,box-shadow,transform] duration-150 active:scale-[0.98] disabled:opacity-45 disabled:active:scale-100',
        size === 'sm' && 'h-8 px-3 text-[13px]',
        size === 'md' && 'h-9 px-3.5 text-sm',
        size === 'lg' && 'h-11 px-5 text-[15px]',
        variant === 'primary' && 'bg-accent text-on-accent shadow-sm hover:bg-accent-2',
        variant === 'secondary' && 'border border-line bg-surface text-fg shadow-sm hover:bg-surface-2',
        variant === 'ghost' && 'text-fg-2 hover:bg-surface-3 hover:text-fg',
        variant === 'danger' && 'bg-danger text-white hover:opacity-90',
        className,
      )}
      {...rest}
    >
      {icon}
      {children}
    </button>
  )
})

interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  label: string
  shortcut?: string
  active?: boolean
  size?: 'sm' | 'md'
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, shortcut, active, size = 'md', className, children, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      aria-label={label}
      aria-pressed={active}
      data-tip={shortcut ? `${label}  ${shortcut}` : label}
      className={clsx(
        'tip relative inline-flex shrink-0 select-none items-center justify-center rounded-xl transition-[background,color,transform] duration-150 active:scale-95 disabled:opacity-35 disabled:active:scale-100',
        size === 'md' ? 'size-9' : 'size-8',
        active ? 'bg-accent-soft text-accent' : 'text-fg-2 hover:bg-surface-3 hover:text-fg',
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  )
})

export function Kbd({ children }: { children: ReactNode }) {
  return (
    <kbd className="inline-flex h-5 min-w-5 items-center justify-center rounded-md border border-line bg-surface-2 px-1.5 font-sans text-[11px] font-medium text-fg-2">
      {children}
    </kbd>
  )
}

export function Divider({ vertical = true }: { vertical?: boolean }) {
  return <div className={vertical ? 'mx-1 h-5 w-px shrink-0 bg-line' : 'my-1 h-px w-full bg-line'} />
}
